import { useCallback, useEffect, useMemo } from 'react';
import { SortingState, Updater } from '@tanstack/react-table';
import useEnvironment from './useEnvironment';

export type CommonTableQueryParams = {
  search: string;
  page: number;
  orderBy: string;
  desc: boolean;
};

interface useSharedTableStateProps {
  pageState: CommonTableQueryParams;
  setPageState: (newState: Partial<CommonTableQueryParams>) => void;
  resetPageState: (keys: string[]) => void;
  orderKeyModifier?: (key: string) => string;
}

type GenericOrderBy = {
  key: string;
  sortOrder: 'asc' | 'desc';
};

export default function useSharedTableState({
  pageState,
  setPageState,
  resetPageState,
  orderKeyModifier
}: useSharedTableStateProps) {
  const { environment } = useEnvironment();

  const sortingState = useMemo<SortingState>(
    () => [{ id: pageState.orderBy, desc: pageState.desc }],
    [pageState.orderBy, pageState.desc],
  );

  const handlePaginationChange = useCallback(
    (page: number) => setPageState({ page }),
    [setPageState],
  );

  const handleSearchChange = useCallback(
    (search: string) => {
      setPageState({ search, page: 1 });
    },
    [setPageState],
  );

  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const value =
        typeof updater === 'function' ? updater(sortingState) : updater;
      if (value?.length) {
        setPageState({
          orderBy: value[0].id,
          desc: value[0].desc,
          page: 1,
        });
      } else {
        resetPageState(['orderBy', 'desc', 'page']);
      }
    },
    [sortingState, setPageState, resetPageState],
  );

  const orderBy = useMemo<GenericOrderBy>(() => {
    const key = orderKeyModifier ? orderKeyModifier(pageState.orderBy) : pageState.orderBy.replace('metrics.', '');
    return {
      key,
      sortOrder: pageState.desc ? 'desc' : 'asc',
    };
  }, [pageState.orderBy, pageState.desc]);

  useEffect(() => {
    if (environment) {
      setPageState({ page: 1 });
    }
  }, [environment?.name]);

  return {
    handlePaginationChange,
    handleSearchChange,
    handleSortingChange,
    page: pageState.page,
    search: pageState.search,
    sortingState,

    orderBy,
  };
}
